import {
    ChildTextContentElement,
    TextContentElement,
} from '@deltek/specification-client/core/Models/textContentElement'
import {
    reactiveWorkspaceActions,
    setReactiveWorkspaceActions,
} from 'api/graphQL/reactiveVars/workspaceActions'
import React from 'react'
import { v4 as newGuid } from 'uuid'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as TextVariants } from '../../constants'
import {
    getParentContentId,
    getUpdatedElements,
} from '../ContentItemElementText'
import { TabsContext } from '../TabsContext'
import { NormalizedDocumentTemplateViewItem } from '../model'
import { getUpdatedUOMInPillTemplate, getUpdatedUOMObject } from '../utils'

export interface InsertUOMPillProps {
    (
        indexToInsert?: number,
        pillText?: string,
        setFieldState?: (input?: any) => void,
        fieldState?: Partial<TextContentElement>,
        template?: NormalizedDocumentTemplateViewItem
    ): void
}

/**
 * `useInsertUomPill` is a custom React hook that provides functionalities related to inserting UOM pills (inside a Pill).
 * the handler `insertUomPill` is published globally via reactiveVars to be used in various components of workspace.
 *
 * @param {Function} setFieldState - func to set the field state.
 * @param {Object} fieldState - The current field state.
 * @param {Object} template - The current template.
 *
 * @returns {Object} An object containing various properties and methods:
 * - `insertUomPill`: The func to insert a UOM pill (inside a pill) at a specified index with a specified text.
 * - `handleUpdateTemplate`: A function to handle updates to the template.
 *
 * @example
 * const { insertUomPill, handleUpdateTemplate } = useInsertUomPill(setFieldState, fieldState, template)
 * insertUomPill(indexToInsert, pillText)
 */
export function useInsertUomPill(
    setFieldState?: (input?: any) => void,
    fieldState?: Partial<TextContentElement>,
    template?: NormalizedDocumentTemplateViewItem
) {
    const { handleUpdateTemplate } = React.useContext(TabsContext)

    const insertUomPill = (indexToInsert?: number, pillText?: string) => {
        const {
            fieldState: reactiveFieldState,
            template: reactiveTemplate,
            changeType,
            // get selected fieldState elements and template
        } = reactiveWorkspaceActions().insertPillRequirements

        fieldState = fieldState ?? reactiveFieldState
        template = template ?? reactiveTemplate

        const updatedElements = getUpdatedElements({
            fieldStateId: fieldState?.id,
            fieldStateText: pillText,
            positionIndex: indexToInsert,
            type: TextVariants.uomInPill,
            fieldStateChangeType: changeType?.toUpperCase() as ChildTextContentElement['changeType'],
            fieldState,
        })

        fieldState!.elements = [
            ...(fieldState?.elements! as Partial<ChildTextContentElement>[]),
            ...(updatedElements as Partial<ChildTextContentElement>[]),
        ]

        setFieldState?.(fieldState!)
        let templateToPass = { ...template! }
        let optionGroupParent = newGuid()
        const noOptionGroup =
            template?.contentItem?.elements?.filter(
                (item) =>
                    item?.type === 'OptionGroup' &&
                    item?.elements?.some(
                        (itemInner) => itemInner?.type === 'newPill'
                    )
            )?.length === 0

        if (noOptionGroup) {
            templateToPass = getUpdatedUOMObject(template!, optionGroupParent)
        }

        handleUpdateTemplate?.(
            getUpdatedUOMInPillTemplate(
                templateToPass,
                fieldState,
                noOptionGroup
                    ? optionGroupParent
                    : getParentContentId(template!, fieldState!, true) ??
                          undefined,
                undefined,
                'Inserting'
            ),
            true
        )
    }

    // side-effect to publish insertPill handler if not already published
    React.useEffect(() => {
        setReactiveWorkspaceActions({
            insertUomPill,
        })
    }, [insertUomPill])

    return {
        insertUomPill,
        handleUpdateTemplate,
    }
}
